var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Renders the list of good damage type inputs grouped by category, and handles
 * picture upload for good damages in a room damage declaration.
 * touches: good damage declaration flow, room damage store (picture collection update)
 * invariants: definitions derived from selectedCategories must map to valid GoodDamageCategoryDefinition;
 * hasEnoughPhotos must reflect requiredPhotos setting per trigram
 * inputs: selectedCategories prop, roomDamage state, damageRequiredPhotosCount setting
 * outputs: emits 'update:photos-valid'; updates goodDamagesPictureCollection in room damage store
 * see: frontend/src/shared/roomDamage/goodDamage.ts,
 * frontend/src/declaration/components/roomDamage/goodDamage/GoodDamageTypesInput.vue
 */
import { namespace } from 'vuex-class';
import { Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { UiCard } from '@claims-ia/design-system';
import { GoodDamagesComponent } from './GoodDamageComponent';
import PictureCollectionUpload from '@declaration/components/upload/PictureCollectionUpload.vue';
import GoodDamageTypesInput from '@declaration/components/roomDamage/goodDamage/GoodDamageTypesInput.vue';
import { getGoodDamageCategoryDefinition, } from '@shared/roomDamage/goodDamage';
import { ClaimFileType } from '@shared/types/file';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
const claim = namespace('claim');
const settings = namespace('settings');
const qualification = namespace('qualification');
let GoodDamageTypesForm = class GoodDamageTypesForm extends GoodDamagesComponent {
    constructor() {
        super(...arguments);
        this.fileType = ClaimFileType.RoomDamage;
    }
    mounted() {
        this.$emit('update:photos-valid', this.hasEnoughPhotos);
    }
    onRequiredPhotosChanged() {
        this.$emit('update:photos-valid', this.hasEnoughPhotos);
    }
    get definitions() {
        return this.selectedCategories
            .map((category) => this.room && getGoodDamageCategoryDefinition(this.room, category))
            .filter((definition) => !!definition);
    }
    get hasDisableDamagesPictures() {
        return insurerHasFeature(InsurerFeatureFlag.DisableDamagesPictures);
    }
    get requiredPhotos() {
        if (!this.trigram || !this.setting)
            return 0;
        let value = null;
        try {
            value = this.setting(`damageRequiredPhotosCount${this.trigram}`);
        }
        catch (_a) { }
        if (value === null || value === undefined) {
            try {
                const grouped = this.setting('damageRequiredPhotosCount');
                if (grouped && typeof grouped === 'object')
                    value = grouped[this.trigram];
            }
            catch (_b) { }
        }
        const count = Number(value);
        return isNaN(count) ? 0 : count;
    }
    get hasEnoughPhotos() {
        var _a, _b;
        if (this.requiredPhotos === 0)
            return true;
        const collection = this.roomDamage.goodDamagesPictureCollection;
        if (!collection || typeof collection === 'string')
            return false;
        return ((_b = (_a = collection.uploadedAppFiles) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) >= this.requiredPhotos;
    }
    onGoodDamagesPictureCollectionUpdate(goodDamagesPictureCollection) {
        this.updateRoomDamage({ goodDamagesPictureCollection });
        this.$nextTick(() => {
            this.$emit('update:photos-valid', this.hasEnoughPhotos);
        });
    }
};
__decorate([
    claim.State('claim')
], GoodDamageTypesForm.prototype, "claim", void 0);
__decorate([
    settings.Getter('setting')
], GoodDamageTypesForm.prototype, "setting", void 0);
__decorate([
    qualification.Getter('typeTrigram')
], GoodDamageTypesForm.prototype, "trigram", void 0);
__decorate([
    Watch('requiredPhotos')
], GoodDamageTypesForm.prototype, "onRequiredPhotosChanged", null);
GoodDamageTypesForm = __decorate([
    Component({
        components: { GoodDamageTypesInput, PictureCollectionUpload, UiCard },
        props: {
            selectedCategories: { type: Array, required: true },
            validateRequested: { type: Boolean, default: false },
        },
    })
], GoodDamageTypesForm);
export default GoodDamageTypesForm;
